/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useRef, useCallback } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Select, MenuItem, FormControl, InputLabel, TextField, Paper, IconButton, Tooltip, Tabs, Tab } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import axios from 'axios';
import { toast } from 'react-toastify';
import NewApplication from '../shared/NewApplication';
import AssignmentManagementPane from './AssignmentManagementPane';
import WateringSchedulePane from './WateringSchedulePane';
import ProjectChat from '../shared/ProjectChat';
import ProjectDocuments from '../shared/ProjectDocuments';
import '../../styles/SingleApplicationReview.css';

const SingleApplicationReview = ({ 
  application, 
  user,
  chatRefreshTrigger,
  onBack, 
  onApprove,
  onReject, 
  onRequestChanges, 
  onAssignChambers, 
  onAssignResources, 
  onRenewProject,
  onSubmit,
  onEndProject,
  onApplicationUpdate
}) => {
  const [formData, setFormData] = useState(application);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [endProjectDialogOpen, setEndProjectDialogOpen] = useState(false);
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [assignmentsExpanded, setAssignmentsExpanded] = useState(false);
  const [wateringExpanded, setWateringExpanded] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(
    application?.project_status === 'Revision Required' || application?.project_status === 'Revised'
  );
  const [leftPaneTab, setLeftPaneTab] = useState('application');
  
  /**
   * Right-pane sizing.
   *
   * The cards used to share the pane's height as percentages, so opening a
   * third one squeezed the activity log to a sliver with nowhere to scroll.
   * Each card now holds a height in pixels and keeps it; the pane itself
   * scrolls when the three together are taller than the screen. Dragging a
   * divider resizes the card above it, and the ones below simply move down.
   *
   * The activity log is last and flexes into whatever is left over, never
   * below CHAT_MIN_HEIGHT — which is what guarantees it stays readable with
   * everything open.
   */
  const MIN_CARD_HEIGHT = 140;
  const CHAT_MIN_HEIGHT = 220;
  const [cardHeights, setCardHeights] = useState({ assignments: 300, watering: 280 });
  const rightPaneRef = useRef(null);
  const cardRefs = useRef({});

  const startResize = useCallback((key) => (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const node = cardRefs.current[key];
    const startHeight = node ? node.getBoundingClientRect().height : MIN_CARD_HEIGHT;

    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent) => {
      const next = Math.max(MIN_CARD_HEIGHT, startHeight + (moveEvent.clientY - startY));
      setCardHeights(prev => ({ ...prev, [key]: next }));
    };

    const handleMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);
  
  // Invoice file naming state
  const [invoiceFileNaming, setInvoiceFileNaming] = useState(application?.invoice_file_naming || '');
  const [isEditingNaming, setIsEditingNaming] = useState(false);
  const [savingNaming, setSavingNaming] = useState(false);

  // Handle saving invoice file naming convention
  const handleSaveInvoiceNaming = async () => {
    setSavingNaming(true);
    try {
      await axios.patch(`/api/projects/${application._id}`, {
        invoice_file_naming: invoiceFileNaming || null
      });
      toast.success('Invoice file naming saved successfully');
      setIsEditingNaming(false);
      // Update the application data
      if (onApplicationUpdate) {
        onApplicationUpdate(application._id);
      }
    } catch (error) {
      console.error('Error saving invoice naming:', error);
      toast.error('Failed to save invoice file naming');
    } finally {
      setSavingNaming(false);
    }
  };

  const handleEndProject = async () => {
    try {
      await axios.patch(`/api/projects/${application._id}/end-project`);
      toast.success('Project ended successfully');
      setEndProjectDialogOpen(false);
      if (onEndProject) {
        onEndProject(application._id);
      }
    } catch (error) {
      console.error('Error ending project:', error);
      toast.error(error.response?.data?.message || 'Failed to end project');
    }
  };

  const handlePermanentDelete = async () => {
    try {
      await axios.delete(`/api/admin/applications/${application._id}`);
      toast.success('Project permanently deleted from the system');
      setPermanentDeleteDialogOpen(false);
      onBack();
    } catch (error) {
      console.error('Error permanently deleting project:', error);
      toast.error(error.response?.data?.error || 'Failed to permanently delete project');
    }
  };

  const handleAssignmentUpdate = async () => {
    // Refresh the application data to reflect assignment changes
    // This ensures the application review page stays current
    try {
      if (onApplicationUpdate) {
        await onApplicationUpdate(application._id);
      }
    } catch (error) {
      console.error('Error refreshing application data:', error);
      toast.error('Failed to refresh page data');
    }
  };

  const handleStatusChange = async () => {
    try {
      const payload = { status: selectedStatus };

      if (selectedStatus === 'In Progress' && needsNewEndDate) {
        if (!newEndDate) {
          toast.error('Please select a new end date in the future');
          return;
        }
        payload.new_end_date = newEndDate;
      }

      const response = await axios.patch(`/api/admin/applications/${application._id}/status`, payload);
      
      if (response.data) {
        toast.success(`Project status changed to ${selectedStatus} successfully`);
        setStatusChangeDialogOpen(false);
        setSelectedStatus('');
        setNewEndDate('');
        
        if (onApplicationUpdate) {
          onApplicationUpdate(application._id);
        }
        
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (error) {
      console.error('Error changing application status:', error);
      toast.error(error.response?.data?.error || 'Failed to change application status');
    }
  };

  const needsNewEndDate = selectedStatus === 'In Progress' && 
    application.project_end_date && 
    new Date(application.project_end_date) <= new Date();

  const handleStatusChangeClick = () => {
    setStatusChangeDialogOpen(true);
  };
  
  const isResourceOnly = application.application_type === 'resource_only';
  
  // Determine project status and available actions
  const status = application.project_status;
  const isSubmitted = status === 'Submitted';
  const isSaved = status === 'Saved';
  const isRevised = status === 'Revised';
  const isRevisionRequired = status === 'Revision Required';
  const isApproved = status === 'Approved' || status === 'In Progress';
  const isSystemRemoval = status === 'System Removal';
  // A resubmitted-after-revision application is reviewed just like a fresh
  // submission: it can be approved, rejected, or sent back for more changes.
  // Submitted, Revised, or still in Revision Required (user saved in place)
  // can be approved, rejected, or sent back for more changes.
  const canApprove = isSubmitted || isRevised || isRevisionRequired;
  const canSubmit = isSaved;
  const canAssignResources = isApproved;
  const canRenew = isApproved && !isResourceOnly;
  const canPermanentDelete = isSystemRemoval;
  const canEndProject = status === 'Approved' || status === 'In Progress';
  // A project past the approval gate. These have space/resources assigned and
  // are eligible for invoicing, so they must NOT be knocked back into a
  // pre-approval status (Revision Required / Submitted / Saved), which would
  // silently remove them from the Project Invoicing tab.
  const isActiveProject = status === 'Approved' || status === 'In Progress' || status === 'Completed';
  // Hide Request Changes when the app is already sitting in Revision Required
  // (the dedicated revision page). Submitted / Revised can still be sent back.
  const canRequestChanges = status !== 'Rejected' && !isActiveProject && !isRevisionRequired;
  
  // Check if project has started (for In Progress status)
  const projectStarted = application.project_start_date 
    ? new Date(application.project_start_date) <= new Date() 
    : false;
  const hasAssignments = (application.chamber_assignments && application.chamber_assignments.length > 0) || 
                        (application.resource_assignments && application.resource_assignments.length > 0);
  
  // Determine if we should show the assignment management pane (two-column layout)
  const showAssignmentPane = status === 'Revision Required' || status === 'Revised' || status === 'Approved' || status === 'In Progress' || status === 'Completed';

  const handleFormChange = (updatedData) => {
    setFormData(updatedData);
  };

  const handleSaveChanges = async (payload) => {
    try {
      const dataToSave = payload ?? formData;
      const response = await fetch(`/api/admin/applications/${application._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSave),
      });

      if (!response.ok) {
        throw new Error('Failed to save changes');
      }

      toast.success('Changes saved successfully!', {
        position: "top-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Redirect back to the applications list after a short delay
      setTimeout(() => {
        window.location.href = '/admin/application';
      }, 1500);
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <Box className="single-application-review">
      {showAssignmentPane ? (
        <div className="review-two-pane">
          {/* Left Column - Application / Documents */}
          <div className="review-left-pane">
            <div className="review-section-card">
              <div className="review-section-header">
                <Tabs value={leftPaneTab} onChange={(e, v) => setLeftPaneTab(v)}>
                  <Tab label="Application" value="application" />
                  <Tab label="Documents" value="documents" />
                </Tabs>
                <span className={`review-status-pill status-${status.toLowerCase().replace(/\s+/g, '-')}`}>
                  {status}
                </span>
                {isApproved && projectStarted && hasAssignments && status !== 'In Progress' && (
                  <span className="review-status-warning">Should be "In Progress"</span>
                )}
              </div>
              <div className="review-section-body">
                {leftPaneTab === 'application' && (
                  <>
                    <div className="action-buttons-row">
                      {canSubmit && (
                        <Button variant="contained" size="small" onClick={onSubmit} color="primary">Submit</Button>
                      )}
                      {canApprove && (
                        <>
                          <Button variant="contained" size="small" className="action-button approve" onClick={onApprove}>Approve</Button>
                          <Button variant="contained" size="small" className="action-button reject" onClick={onReject}>Reject</Button>
                        </>
                      )}
                      {canRequestChanges && (
                        <Button variant="contained" size="small" className="action-button request-changes" onClick={onRequestChanges}>Request Changes</Button>
                      )}
                      <Button variant="contained" size="small" className="action-button change-status" onClick={handleStatusChangeClick} startIcon={<ChangeCircleIcon sx={{ fontSize: 16 }} />} color="secondary">
                        Change Status
                      </Button>
                      {canAssignResources && (
                        <>
                          {!isResourceOnly && (
                            <Button variant="contained" size="small" className="action-button assign" onClick={onAssignChambers}>Assign Compartments</Button>
                          )}
                          <Button variant="contained" size="small" className="action-button assign" onClick={onAssignResources}>Assign Resources</Button>
                        </>
                      )}
                      {canRenew && (
                        <Button variant="contained" size="small" className="action-button renew" onClick={onRenewProject}>Renew</Button>
                      )}
                      {canEndProject && (
                        <Button variant="contained" size="small" className="action-button danger" color="error" onClick={() => setEndProjectDialogOpen(true)}>End Project</Button>
                      )}
                      {canPermanentDelete && (
                        <Button variant="contained" size="small" className="action-button danger" color="error" startIcon={<DeleteIcon sx={{ fontSize: 16 }} />} onClick={() => setPermanentDeleteDialogOpen(true)}>Delete</Button>
                      )}
                      <Button variant="outlined" size="small" className="action-button back" onClick={onBack}>Back to List</Button>
                    </div>

                    {status !== 'Saved' && (
                      <div className="invoice-naming-row">
                        <span className="invoice-naming-label">Invoice File Naming:</span>
                        {isEditingNaming ? (
                          <div className="invoice-naming-edit">
                            <TextField
                              size="small"
                              value={invoiceFileNaming}
                              onChange={(e) => setInvoiceFileNaming(e.target.value)}
                              placeholder="e.g., Smith_SoilResearch"
                              sx={{ flex: 1, maxWidth: 220 }}
                              disabled={savingNaming}
                            />
                            <span className="invoice-naming-suffix">_Invoice_#</span>
                            <Tooltip title="Save">
                              <IconButton color="primary" onClick={handleSaveInvoiceNaming} disabled={savingNaming} size="small"><SaveIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Button size="small" onClick={() => { setInvoiceFileNaming(application?.invoice_file_naming || ''); setIsEditingNaming(false); }} disabled={savingNaming}>Cancel</Button>
                          </div>
                        ) : (
                          <div className="invoice-naming-display">
                            <span>{application?.invoice_file_naming ? `${application.invoice_file_naming}_Invoice_#` : 'Not set'}</span>
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => setIsEditingNaming(true)}><EditIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="review-form-scroll">
                      {isResourceOnly ? (
                        <div className="resource-only-review" style={{ padding: '16px' }}>
                          <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                            <Typography variant="subtitle2" sx={{ color: '#1565c0', fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              Resource Only Request
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{application.project_title}</Typography>
                            <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                              <strong>Facility:</strong> {application.facility?.facility_name || 'Not specified'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                              <strong>People:</strong> {application.users?.length
                        ? application.users.map(u => `${u.first_name} ${u.last_name} (${u.email})${u.role?.role ? ` - ${u.role.role}` : ''}`).join('; ')
                        : 'Unknown'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              <strong>Submitted:</strong> {application.createdAt ? new Date(application.createdAt).toLocaleDateString() : 'N/A'}
                            </Typography>
                          </Paper>

                          {application.resource_request_purpose && (
                            <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Purpose</Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{application.resource_request_purpose}</Typography>
                            </Paper>
                          )}

                          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Requested Resources</Typography>
                            {application.requested_resources && application.requested_resources.length > 0 ? (
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                                    <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Resource</th>
                                    <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Quantity</th>
                                    <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Unit</th>
                                    <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {application.requested_resources.map((rr, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                      <td style={{ padding: '8px', fontSize: '0.9rem' }}>{rr.resource?.resource || rr.resource?.name || 'Unknown'}</td>
                                      <td style={{ padding: '8px', fontSize: '0.9rem' }}>{rr.quantity}</td>
                                      <td style={{ padding: '8px', fontSize: '0.9rem' }}>{rr.resource?.unit?.base_unit || rr.resource?.unit?.unit_short || '—'}</td>
                                      <td style={{ padding: '8px', fontSize: '0.9rem', color: '#666' }}>{rr.notes || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#999' }}>No resources requested.</Typography>
                            )}
                          </Paper>
                        </div>
                      ) : (
                        <NewApplication 
                          mode="edit"
                          initialData={application}
                          user={null}
                          applicationId={application._id}
                          isAdminReview={true}
                          onFormChange={handleFormChange}
                          onSaveConfirm={handleSaveChanges}
                          hideFormHeading={true}
                        />
                      )}
                    </div>
                  </>
                )}

                {leftPaneTab === 'documents' && (
                  <div className="review-documents-scroll">
                    <ProjectDocuments projectId={application._id} user={user} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="review-right-pane" ref={rightPaneRef}>
            {/* Assigned Items */}
            <div
              ref={(el) => { cardRefs.current.assignments = el; }}
              className={`review-section-card review-assignments-card ${assignmentsExpanded ? 'expanded' : 'collapsed'}`}
              style={assignmentsExpanded ? { flex: `0 0 ${cardHeights.assignments}px` } : undefined}
            >
              <div className="review-section-header clickable" onClick={() => setAssignmentsExpanded(!assignmentsExpanded)}>
                <span className="review-section-title">Assigned Items</span>
                <IconButton aria-label="Expand or collapse" size="small">
                  {assignmentsExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                </IconButton>
              </div>
              {assignmentsExpanded && (
                <div className="review-assignments-body">
                  <AssignmentManagementPane
                    application={application}
                    onAssignChambers={onAssignChambers}
                    onAssignResources={onAssignResources}
                    onAssignmentUpdate={handleAssignmentUpdate}
                  />
                </div>
              )}
            </div>

            {/* Drag to resize Assigned Items. Only shown when something below
                it is open, since there is nothing to make room for otherwise. */}
            {assignmentsExpanded && (wateringExpanded || chatExpanded) && (
              <div
                className="review-pane-divider"
                onMouseDown={startResize('assignments')}
                role="separator"
                aria-label="Resize Assigned Items"
              >
                <div className="review-pane-divider-handle" />
              </div>
            )}

            {/* Watering schedules: effective-dated, so this shows what is in
                effect and what it used to be. Clients ask for changes through a
                Compartment Environmental Adjustment request; an admin records
                the outcome here. */}
            <div
              ref={(el) => { cardRefs.current.watering = el; }}
              className={`review-section-card review-watering-card ${wateringExpanded ? 'expanded' : 'collapsed'}`}
              style={wateringExpanded ? { flex: `0 0 ${cardHeights.watering}px` } : undefined}
            >
              <div className="review-section-header clickable" onClick={() => setWateringExpanded(!wateringExpanded)}>
                <span className="review-section-title">Watering Schedules</span>
                <IconButton aria-label="Expand or collapse" size="small">
                  {wateringExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                </IconButton>
              </div>
              {wateringExpanded && (
                <div className="review-watering-body">
                  <WateringSchedulePane
                    projectId={application._id}
                    facilityId={application.facility?._id || application.facility || null}
                  />
                </div>
              )}
            </div>

            {/* Drag to resize Watering Schedules. */}
            {wateringExpanded && chatExpanded && (
              <div
                className="review-pane-divider"
                onMouseDown={startResize('watering')}
                role="separator"
                aria-label="Resize Watering Schedules"
              >
                <div className="review-pane-divider-handle" />
              </div>
            )}

            {/* Activity Log */}
            <div
              className={`review-section-card review-chat-card ${chatExpanded ? 'expanded' : 'collapsed'}`}
              style={chatExpanded ? { minHeight: CHAT_MIN_HEIGHT } : undefined}
            >
              <div className="review-section-header clickable" onClick={() => setChatExpanded(!chatExpanded)}>
                <span className="review-section-title">
                  {(status === 'Revision Required' || status === 'Revised') ? 'Revision Discussion' : 'Activity Log'}
                </span>
                <IconButton aria-label="Expand or collapse" size="small">
                  {chatExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                </IconButton>
              </div>
              {chatExpanded && (
                <div className="review-chat-body">
                  <ProjectChat
                    projectId={application._id}
                    user={user}
                    maxHeight="100%"
                    refreshTrigger={chatRefreshTrigger}
                    channel="activity"
                    allowComments={status === 'Revision Required' || status === 'Revised'}
                    hideTeamReminder={status !== 'Revision Required' && status !== 'Revised'}
                    commentPlaceholder="Comment on the requested revisions..."
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // Single-pane layout for Saved and Submitted
        <Box sx={{ padding: 3, height: '100%', overflow: 'auto' }}>
          <Box className="action-buttons-container">
            <div className="status-info">
              <span className={`current-status status-${status.toLowerCase().replace(' ', '-')}`}>
                Current Status: {status}
              </span>
              {isApproved && projectStarted && hasAssignments && status !== 'In Progress' && (
                <span className="status-note">
                  ⚠️ Project should be marked as "In Progress"
                </span>
              )}
            </div>
            
            <div className="action-buttons">
              {canSubmit && (
                <Button variant="contained" className="action-button submit" onClick={onSubmit} color="primary">
                  Submit Application
                </Button>
              )}
              {canApprove && (
                <>
                  <Button variant="contained" className="action-button approve" onClick={onApprove}>Approve</Button>
                  <Button variant="contained" className="action-button reject" onClick={onReject}>Reject</Button>
                </>
              )}
              {canRequestChanges && (
                <Button variant="contained" className="action-button request-changes" onClick={onRequestChanges}>Request Changes</Button>
              )}
              {canPermanentDelete && (
                <Button variant="contained" color="error" className="action-button permanent-delete" startIcon={<DeleteIcon />} onClick={() => setPermanentDeleteDialogOpen(true)}>
                  Permanently Delete
                </Button>
              )}
              <Button variant="contained" className="action-button change-status" onClick={handleStatusChangeClick} startIcon={<ChangeCircleIcon />} color="secondary">
                Change Status
              </Button>
              <Button variant="outlined" className="action-button back" onClick={onBack}>Back to List</Button>
            </div>
          </Box>

          <Tabs
            value={leftPaneTab}
            onChange={(e, v) => setLeftPaneTab(v)}
            sx={{
              mb: 2,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' },
            }}
          >
            <Tab label="Application" value="application" />
            <Tab label="Documents" value="documents" />
          </Tabs>

          {leftPaneTab === 'application' && (
            <>
              {status !== 'Saved' && (
                <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: 180 }}>
                      Invoice File Naming:
                    </Typography>
                    {isEditingNaming ? (
                      <>
                        <TextField
                          size="small"
                          value={invoiceFileNaming}
                          onChange={(e) => setInvoiceFileNaming(e.target.value)}
                          placeholder="e.g., Smith_SoilResearch"
                          sx={{ flex: 1, maxWidth: 300 }}
                          disabled={savingNaming}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          _Invoice_#
                        </Typography>
                        <Tooltip title="Save">
                          <IconButton color="primary" onClick={handleSaveInvoiceNaming} disabled={savingNaming} size="small">
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Button size="small" onClick={() => { setInvoiceFileNaming(application?.invoice_file_naming || ''); setIsEditingNaming(false); }} disabled={savingNaming}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {application?.invoice_file_naming ? (
                            <>{application.invoice_file_naming}_Invoice_#</>
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>Not set (will use default naming)</span>
                          )}
                        </Typography>
                        <Tooltip title="Edit naming convention">
                          <IconButton size="small" onClick={() => setIsEditingNaming(true)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 0 }}>
                    Format: Lastname_Keyword — The "_Invoice_#" will be appended automatically with the invoice number
                  </Typography>
                </Paper>
              )}

              {isResourceOnly ? (
                <div className="resource-only-review" style={{ padding: '8px 0' }}>
                  <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                    <Typography variant="subtitle2" sx={{ color: '#1565c0', fontWeight: 700, mb: 1 }}>
                      Resource Only Request
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{application.project_title}</Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      <strong>Facility:</strong> {application.facility?.facility_name || 'Not specified'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      <strong>People:</strong> {application.users?.length
                        ? application.users.map(u => `${u.first_name} ${u.last_name} (${u.email})${u.role?.role ? ` - ${u.role.role}` : ''}`).join('; ')
                        : 'Unknown'}
                    </Typography>
                  </Paper>

                  {application.resource_request_purpose && (
                    <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Purpose</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{application.resource_request_purpose}</Typography>
                    </Paper>
                  )}

                  <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Requested Resources</Typography>
                    {application.requested_resources && application.requested_resources.length > 0 ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                            <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Resource</th>
                            <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Quantity</th>
                            <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Unit</th>
                            <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {application.requested_resources.map((rr, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '8px', fontSize: '0.9rem' }}>{rr.resource?.resource || 'Unknown'}</td>
                              <td style={{ padding: '8px', fontSize: '0.9rem' }}>{rr.quantity}</td>
                              <td style={{ padding: '8px', fontSize: '0.9rem' }}>{rr.resource?.unit?.base_unit || '—'}</td>
                              <td style={{ padding: '8px', fontSize: '0.9rem', color: '#666' }}>{rr.notes || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#999' }}>No resources requested.</Typography>
                    )}
                  </Paper>
                </div>
              ) : (
                <NewApplication 
                  mode="edit"
                  initialData={application}
                  user={null}
                  applicationId={application._id}
                  isAdminReview={true}
                  onFormChange={handleFormChange}
                  onSaveConfirm={handleSaveChanges}
                  hideFormHeading={true}
                />
              )}
            </>
          )}

          {leftPaneTab === 'documents' && (
            <Paper sx={{ p: 2 }}>
              <ProjectDocuments projectId={application._id} user={user} />
            </Paper>
          )}
        </Box>
      )}

      {/* End Project Confirmation Dialog */}
      <Dialog
        open={endProjectDialogOpen}
        onClose={() => setEndProjectDialogOpen(false)}
        aria-labelledby="end-project-dialog-title"
        aria-describedby="end-project-dialog-description"
      >
        <DialogTitle id="end-project-dialog-title">
          End Project?
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Are you sure you want to end this project? This action will:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Set the project status to "Completed"</li>
            <li>Update the project end date to today</li>
            <li>End all active compartment allocations</li>
            <li>Set all compartment allocation end dates to today</li>
          </Typography>
          <Typography paragraph sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndProjectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEndProject} color="error" variant="contained">
            End Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Confirmation Dialog */}
      <Dialog
        open={statusChangeDialogOpen}
        onClose={() => setStatusChangeDialogOpen(false)}
        aria-labelledby="status-change-dialog-title"
        aria-describedby="status-change-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="status-change-dialog-title">
          Change Application Status
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Current Status: <strong>{status}</strong>
          </Typography>
          <Typography paragraph>
            Select the new status for this application:
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="status-select-label">New Status</InputLabel>
            <Select
              labelId="status-select-label"
              value={selectedStatus}
              label="New Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {/* Pre-approval statuses are hidden for active projects so an
                  invoiceable project can't be knocked out of invoicing. */}
              {!isActiveProject && <MenuItem value="Saved">Saved</MenuItem>}
              {!isActiveProject && <MenuItem value="Submitted">Submitted</MenuItem>}
              {!isActiveProject && <MenuItem value="Revision Required">Revision Required</MenuItem>}
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="System Removal">System Removal</MenuItem>
            </Select>
          </FormControl>
          {needsNewEndDate && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: 'warning.dark' }}>
                The current project end date ({new Date(application.project_end_date).toLocaleDateString()}) has already passed.
                Please set a new end date in the future to transfer this project back to In Progress.
              </Typography>
              <TextField
                label="New End Date"
                type="date"
                fullWidth
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date(new Date().getTime() + 86400000).toISOString().split('T')[0] }}
                sx={{ mt: 1, bgcolor: 'white', borderRadius: 1 }}
              />
            </Box>
          )}
          {selectedStatus && !needsNewEndDate && (
            <Typography paragraph sx={{ mt: 2, fontWeight: 'bold', color: 'primary.main' }}>
              This project will be moved to the "{selectedStatus}" tab.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setStatusChangeDialogOpen(false);
            setSelectedStatus('');
            setNewEndDate('');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleStatusChange} 
            color="primary" 
            variant="contained"
            disabled={!selectedStatus || (needsNewEndDate && !newEndDate)}
          >
            {needsNewEndDate ? 'Update & Transfer to In Progress' : 'Change Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog
        open={permanentDeleteDialogOpen}
        onClose={() => setPermanentDeleteDialogOpen(false)}
        aria-labelledby="permanent-delete-dialog-title"
        aria-describedby="permanent-delete-dialog-description"
      >
        <DialogTitle id="permanent-delete-dialog-title" sx={{ color: 'error.main' }}>
          Permanently Delete Project?
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            This will permanently delete the project "{application.project_title}" ({application.project_id}) and all its data from the database.
          </Typography>
          <Typography paragraph>
            Compartment space usage records will be preserved for historical reporting and will display as "{application.project_id} (deleted)" in compartment reports.
          </Typography>
          <Typography paragraph sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermanentDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePermanentDelete} color="error" variant="contained">
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SingleApplicationReview; 